/**
 * Stale-chunk recovery for the code-split SPA.
 *
 * The portal is split into many hashed lazy chunks (one per feature route)
 * and served from an Azure Static Web App. On redeploy the app ships new
 * chunk hashes and deletes the old ones. A tab still running the previous
 * build holds the old `index.html`; navigating to a route whose chunk wasn't
 * fetched yet requests a now-deleted hashed URL that 404s, and the dynamic
 * `import()` rejects. "Try again" can't recover because the rejected module is
 * cached and re-throws with no new request — only a full reload (which pulls
 * the fresh `index.html` + chunk manifest) fixes the tab.
 *
 * Two entry points feed the SAME guard so they coordinate (one reload, no
 * flash, no loop):
 *   - `installChunkReloadRecovery()` listens for Vite's `vite:preloadError`
 *     (covers any dynamic import, incl. non-route ones like `xlsx`/modals).
 *   - `lazyWithReload` (see ./lazyWithReload) catches the rejection of a route
 *     chunk and, while a reload is pending, keeps `<Suspense>` showing its
 *     loading spinner instead of surfacing the FeatureErrorBoundary card.
 */

const RELOAD_KEY = 'vite:last-preload-reload';
const RELOAD_DEBOUNCE_MS = 10_000;

/**
 * Set once we kick off a reload on this page-life so every *later* chunk error
 * in the same page knows a reload is already coming — and reports `true`
 * (show a spinner) rather than racing a second reload or surfacing an error.
 */
let reloadInitiated = false;

/** Test-only: reset the module-level reload latch. */
export function resetChunkReloadStateForTests(): void {
  reloadInitiated = false;
}

/**
 * Decide how to handle a stale-chunk failure and trigger recovery.
 *
 * @returns `true` when a reload is happening/imminent (caller should show a
 * benign loading state, NOT an error); `false` when this is a genuine failure
 * that already survived a recent reload (caller should surface the error).
 */
export function maybeReloadForStaleChunk(): boolean {
  if (reloadInitiated) return true;

  try {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? '0');
    // A chunk error recurring within the debounce window means a reload already
    // happened and the *fresh* build still can't load the chunk — a real
    // failure, not a stale-build mismatch. Surface it instead of looping.
    if (now - last < RELOAD_DEBOUNCE_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // sessionStorage blocked (private mode / sandboxed iframe) — we lose loop
    // protection, but transparent recovery matters more, so fall through.
  }

  reloadInitiated = true;
  window.location.reload();
  return true;
}

export function installChunkReloadRecovery(): void {
  window.addEventListener('vite:preloadError', () => {
    maybeReloadForStaleChunk();
  });
}
